/**
 * The three.js stage: renderer, camera, orbit controls, lights, ground reference, and the two
 * top-level groups everything else hangs off.
 *
 * The group split is load-bearing rather than cosmetic. `modelGroup` holds the imported model and
 * nothing else; `overlayGroup` holds every editor-only object (skeleton dots, gizmo proxies, IK
 * handles, the heat mesh). The GLB exporter walks `session.model.root`, so a helper that ever
 * became a child of the model would silently ship inside the user's exported character.
 */

import {
  AxesHelper,
  Color,
  DirectionalLight,
  GridHelper,
  Group,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import type { Box3, Object3D } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

import { AppError } from '../../shared';
import type { CameraPreset } from '../context';

/*
 * The viewport ground is the CSS gradient on `.asx-viewport` (`design/ui-design.md` §4), which the
 * renderer can only show through: hence `alpha: true`, a zero-alpha clear and `scene.background =
 * null`. `BACKGROUND` stays as the opaque fallback used when a colour is genuinely needed (it is
 * the midpoint of that gradient), so nothing renders black if the CSS ever fails to load.
 */
const BACKGROUND = 0x2f2f2f;
/** Ground grid: the two steps of `--edge-2` / `--widget-off`, never lighter than the model. */
const GRID_MAJOR = 0x4a4a4a;
const GRID_MINOR = 0x3a3a3a;
const GROUND_SIZE = 4;
const GROUND_DIVISIONS = 16;
const DEFAULT_RADIUS = 1.8;

export interface ViewportScene {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  controls: OrbitControls;
  /** The imported model lives here — never add helpers to it. */
  modelGroup: Group;
  /** Editor-only objects. Everything in here is marked `userData.editorOnly`. */
  overlayGroup: Group;
  /** Resize to the container's current client box. Safe to call when the box is 0×0. */
  resize(): void;
  /**
   * Called after the ResizeObserver has resized the renderer. Returns an unsubscribe.
   *
   * `renderer.setSize` CLEARS the drawing buffer, so an observed resize that nobody repaints
   * leaves the viewport blank until the next unrelated store change (audit A6 — every survey
   * screenshot after the first window size showed an empty viewport). `mount.ts` subscribes its
   * `requestRender` here; the handle's own `resize()` already goes through a caller that repaints.
   */
  onResize(cb: () => void): () => void;
  setCameraPreset(preset: CameraPreset): void;
  /** Fit the camera to `bounds`, or to a default 1.8 m box when nothing is loaded. */
  frameBounds(bounds: Box3 | null): void;
  render(): void;
  dispose(): void;
}

/** Tag an object (and everything under it) as an editor helper. */
export function markEditorOnly<T extends Object3D>(object: T): T {
  object.traverse((o) => {
    o.userData.editorOnly = true;
  });
  return object;
}

/**
 * The message the user actually reads when the viewport cannot start.
 *
 * It is written out in full here rather than left to `src/main.ts`, because the entry point's
 * startup fallback renders `AppError.message` verbatim for ANY startup failure — so this string
 * is the whole explanation the user gets, and it has to name the likely cause and the fix.
 */
const NO_WEBGL_MESSAGE =
  'This browser could not start WebGL, which the 3D viewport needs. Hardware acceleration is ' +
  'most likely switched off (in Chrome: Settings → System → "Use graphics acceleration when ' +
  'available"), or the graphics driver is blocklisted. Turn it on and reload the page.';

/**
 * Can this browser actually give us a WebGL context?
 *
 * Until 2026-08-22 nothing asked: `new WebGLRenderer()` simply threw on a Chrome with hardware
 * acceleration disabled, and because `installErrorHandlers` was wired three lines LATER in
 * `src/main.ts` the throw escaped into nothing — the shell was painted, the viewport and the
 * timeline never mounted, and the user was left staring at half a page with no message at all
 * (audit §2.6 #2). The probe exists so that failure becomes a named `AppError` the entry point
 * can turn into a readable page.
 */
function hasWebGLSupport(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as
      | WebGLRenderingContext
      | null;
    if (!gl) return false;
    // Hand the probe's context straight back: browsers cap the number of live WebGL contexts
    // (~16 in Chrome) and the real renderer is about to ask for one of them.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    // `getContext` itself throwing is rare but real (some locked-down/headless builds do it).
    // A probe that cannot answer means "no viewport", which is exactly what `false` says here.
    return false;
  }
}

/**
 * Construct the renderer, or fail with a code the entry point understands.
 *
 * Both halves matter: the probe catches the common "acceleration is off" case up front, and the
 * try/catch catches the rarer case where a context can be created for a throwaway canvas but the
 * renderer still fails (driver reset, too many live contexts). Either way the caller sees one
 * `AppError` code instead of an opaque three.js exception.
 */
function createRenderer(): WebGLRenderer {
  if (!hasWebGLSupport()) throw new AppError('VIEWPORT_NO_WEBGL', NO_WEBGL_MESSAGE);
  try {
    return new WebGLRenderer({ antialias: true, alpha: true });
  } catch (cause) {
    throw new AppError('VIEWPORT_NO_WEBGL', NO_WEBGL_MESSAGE, { cause: String(cause) });
  }
}

/** Constructor shape of `ResizeObserver`, so a test can hand in a fake one. */
export type ResizeObserverCtor = new (callback: ResizeObserverCallback) => ResizeObserver;

export interface ResizeBroadcast {
  /** Register a post-resize callback; returns an unsubscribe. */
  onResize(cb: () => void): () => void;
  dispose(): void;
}

/**
 * Observe `container` and, on every observed change, resize and then tell everyone.
 *
 * Split out of `createViewportScene` for one reason: the scene needs a live WebGL context, which
 * jsdom cannot give, so the repaint contract of audit A6 would otherwise be untestable. This is
 * the function the scene actually uses — the test injects a fake observer constructor, not a
 * second implementation.
 *
 * The listener set is copied before iterating: a callback that unsubscribes itself (dispose
 * racing a resize) must not skip the next listener.
 */
export function createResizeBroadcast(
  container: HTMLElement,
  resize: () => void,
  ObserverCtor: ResizeObserverCtor | null = typeof ResizeObserver === 'undefined'
    ? null
    : ResizeObserver,
): ResizeBroadcast {
  const listeners = new Set<() => void>();

  const observer = ObserverCtor
    ? new ObserverCtor(() => {
        resize();
        for (const cb of [...listeners]) cb();
      })
    : null;
  observer?.observe(container);

  return {
    onResize(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    dispose() {
      observer?.disconnect();
      listeners.clear();
    },
  };
}

export function createViewportScene(container: HTMLElement): ViewportScene {
  const renderer = createRenderer();
  renderer.setClearColor(new Color(BACKGROUND), 0);
  renderer.domElement.classList.add('asx-viewport-canvas');
  renderer.domElement.tabIndex = 0;
  container.appendChild(renderer.domElement);

  const scene = new Scene();
  // Transparent: `.asx-viewport`'s gradient is the background (see the note on BACKGROUND).
  scene.background = null;

  const camera = new PerspectiveCamera(45, 1, 0.01, 200);
  camera.position.set(2.2, 1.6, 3.2);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = false;
  controls.target.set(0, 0.9, 0);
  controls.update();

  const modelGroup = new Group();
  modelGroup.name = 'asx-model';
  scene.add(modelGroup);

  const overlayGroup = new Group();
  overlayGroup.name = 'asx-overlay';
  overlayGroup.userData.editorOnly = true;
  scene.add(overlayGroup);

  const grid = new GridHelper(GROUND_SIZE, GROUND_DIVISIONS, GRID_MAJOR, GRID_MINOR);
  grid.userData.editorOnly = true;
  const axes = new AxesHelper(0.5);
  axes.userData.editorOnly = true;
  scene.add(grid, axes);

  const hemi = new HemisphereLight(0xffffff, 0x444444, 2.0);
  const dir = new DirectionalLight(0xffffff, 1.6);
  dir.position.set(2, 4, 3);
  scene.add(hemi, dir);

  // --- sizing -------------------------------------------------------------

  function resize(): void {
    const w = Math.max(1, container.clientWidth);
    const h = Math.max(1, container.clientHeight);
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  const resizes = createResizeBroadcast(container, resize);
  resize();

  // --- framing / presets --------------------------------------------------

  const center = new Vector3(0, 0.9, 0);
  let radius = DEFAULT_RADIUS;

  function applyDistance(direction: Vector3): void {
    // Enough distance that a sphere of `radius` fits the *narrower* of the two FOVs.
    const vFov = (camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
    const distance = radius / Math.sin(Math.max(0.1, Math.min(vFov, hFov)) / 2);
    camera.position.copy(center).addScaledVector(direction, distance);
    controls.target.copy(center);
    camera.near = Math.max(0.005, distance / 500);
    camera.far = distance * 20;
    camera.updateProjectionMatrix();
    controls.update();
  }

  const PRESET_DIRECTIONS: Record<CameraPreset, Vector3> = {
    // Left is +X and the character faces +Z (D4), so "front" looks from +Z back at the model.
    persp: new Vector3(0.7, 0.45, 1).normalize(),
    front: new Vector3(0, 0, 1),
    side: new Vector3(-1, 0, 0),
    top: new Vector3(0, 1, 0.0001).normalize(),
  };

  function setCameraPreset(preset: CameraPreset): void {
    applyDistance(PRESET_DIRECTIONS[preset]);
  }

  function frameBounds(bounds: Box3 | null): void {
    if (bounds && !bounds.isEmpty()) {
      bounds.getCenter(center);
      const size = bounds.getSize(new Vector3());
      radius = Math.max(0.1, Math.max(size.x, size.y, size.z) * 0.62);
    } else {
      center.set(0, 0.9, 0);
      radius = DEFAULT_RADIUS;
    }
    // Keep the current viewing direction; only re-centre and re-distance.
    const direction = camera.position.clone().sub(controls.target);
    if (direction.lengthSq() < 1e-9) direction.copy(PRESET_DIRECTIONS.persp);
    applyDistance(direction.normalize());
  }

  function render(): void {
    renderer.render(scene, camera);
  }

  function dispose(): void {
    resizes.dispose();
    controls.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  }

  return {
    renderer,
    scene,
    camera,
    controls,
    modelGroup,
    overlayGroup,
    resize,
    onResize: resizes.onResize,
    setCameraPreset,
    frameBounds,
    render,
    dispose,
  };
}
